import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  HostListener,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';

import { SidebarComponent } from '@core/component';
import { LicenseBannerComponent } from '@core/component';
import { ExpiredOverlayComponent } from '@core/component';
import { LicenseService } from '@core/services';

/** Matches the $bp-mobile breakpoint used across the component stylesheets. */
const MOBILE_BREAKPOINT = 768;

@Component({
  imports: [RouterOutlet, CommonModule, SidebarComponent, LicenseBannerComponent, ExpiredOverlayComponent],
  templateUrl: './main.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrl: './main.component.scss',
})
export class MainComponent implements OnInit, OnDestroy {
  isSidebarCollapsed = false;
  isOnboarding = false;
  isChoosePlan = false;

  /** True while the viewport is phone-sized; drives the off-canvas nav. */
  isMobile = false;
  /** Whether the off-canvas nav drawer is currently open (mobile only). */
  isMobileNavOpen = false;

  private _routerSub?: Subscription;

  constructor(
    private readonly _router: Router,
    private readonly _licenseService: LicenseService,
  ) {}

  ngOnInit(): void {
    this._updateIsMobile();

    this._checkRoute(this._router.url);
    this._routerSub = this._router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this._checkRoute(e.urlAfterRedirects);
        // Navigating from the drawer should always dismiss it.
        this.closeMobileNav();
      });
  }

  ngOnDestroy(): void {
    this._routerSub?.unsubscribe();
    this._setBodyScrollLock(false);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this._updateIsMobile();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isMobileNavOpen) {
      this.closeMobileNav();
    }
  }

  onSidebarToggle() {
    // On phones the same control opens/closes the drawer instead of collapsing.
    if (this.isMobile) {
      this.toggleMobileNav();
      return;
    }

    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  toggleMobileNav(): void {
    this.isMobileNavOpen = !this.isMobileNavOpen;
    this._setBodyScrollLock(this.isMobileNavOpen);
  }

  closeMobileNav(): void {
    if (!this.isMobileNavOpen) {
      return;
    }

    this.isMobileNavOpen = false;
    this._setBodyScrollLock(false);
  }

  goToDashboard(): void {
    this._router.navigate(['/dashboard']);
  }

  private _updateIsMobile(): void {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    if (isMobile === this.isMobile) {
      return;
    }

    this.isMobile = isMobile;

    // Leaving mobile must not strand an open drawer or a locked body.
    if (!isMobile) {
      this.isMobileNavOpen = false;
      this._setBodyScrollLock(false);
    }
  }

  private _setBodyScrollLock(locked: boolean): void {
    document.body.classList.toggle('mobile-nav-open', locked);
  }

  private _checkRoute(url: string): void {
    this.isOnboarding = url.startsWith('/onboarding');
    this.isChoosePlan = url.startsWith('/choose-plan');
  }
}
